/**
 * journey-fonts — typeface registry for the Journeys branding + renderer.
 * Verbatim port of the source prototype's fonts.js (window.WFM_FONTS) into a
 * plain ES module. Each family loads via its OWN <link>/<style> so one bad
 * custom font can't break the rest. ui-config (typeface catalog + loader).
 */
type FontEntry = {
  id: string; cat: string; note?: string;
  google?: string; family?: string; stack?: string;
  kind?: "google" | "url"; spec?: string; value?: string; fallback?: string;
};

const CAT_ORDER = ["Sans-serif", "Serif", "Display", "Monospace", "System", "Custom"];

const BUILTIN: FontEntry[] = [
  { id: "DM Sans", cat: "Sans-serif", note: "Neutral · modern", google: "DM+Sans:wght@400;500;600;700", stack: "system-ui, sans-serif" },
  { id: "Inter", cat: "Sans-serif", note: "Workhorse · UI", google: "Inter:wght@400;500;600;700", stack: "system-ui, sans-serif" },
  { id: "Manrope", cat: "Sans-serif", note: "Rounded · geometric", google: "Manrope:wght@400;500;600;700;800", stack: "system-ui, sans-serif" },
  { id: "Plus Jakarta Sans", cat: "Sans-serif", note: "Contemporary · soft", google: "Plus+Jakarta+Sans:wght@400;500;600;700", stack: "system-ui, sans-serif" },
  { id: "Work Sans", cat: "Sans-serif", note: "Grotesque · calm", google: "Work+Sans:wght@400;500;600;700", stack: "system-ui, sans-serif" },
  { id: "Space Grotesk", cat: "Sans-serif", note: "Technical · friendly", google: "Space+Grotesk:wght@400;500;600;700", stack: "system-ui, sans-serif" },
  { id: "Poppins", cat: "Sans-serif", note: "Geometric · bold", google: "Poppins:wght@400;500;600;700", stack: "system-ui, sans-serif" },
  { id: "Albert Sans", cat: "Sans-serif", note: "Clean · low-contrast", google: "Albert+Sans:wght@400;500;600;700", stack: "system-ui, sans-serif" },
  { id: "Playfair Display", cat: "Serif", note: "Editorial · high-contrast", google: "Playfair+Display:wght@500;600;700;800", stack: "Georgia, serif" },
  { id: "Fraunces", cat: "Serif", note: "Characterful · warm", google: "Fraunces:wght@400;500;600;700", stack: "Georgia, serif" },
  { id: "Lora", cat: "Serif", note: "Readable · balanced", google: "Lora:wght@400;500;600;700", stack: "Georgia, serif" },
  { id: "Cormorant Garamond", cat: "Serif", note: "Elegant · display serif", google: "Cormorant+Garamond:wght@400;500;600;700", stack: "Georgia, serif" },
  { id: "Source Serif 4", cat: "Serif", note: "Traditional · clean", google: "Source+Serif+4:wght@400;500;600;700", stack: "Georgia, serif" },
  { id: "Spectral", cat: "Serif", note: "Literary · refined", google: "Spectral:wght@400;500;600;700", stack: "Georgia, serif" },
  { id: "IBM Plex Serif", cat: "Serif", note: "Humanist · sturdy", google: "IBM+Plex+Serif:wght@400;500;600;700", stack: "Georgia, serif" },
  { id: "Bricolage Grotesque", cat: "Display", note: "Expressive · chunky", google: "Bricolage+Grotesque:wght@400;500;600;700;800", stack: "system-ui, sans-serif" },
  { id: "Syne", cat: "Display", note: "Avant-garde · wide", google: "Syne:wght@400;500;600;700;800", stack: "system-ui, sans-serif" },
  { id: "Unbounded", cat: "Display", note: "Bold · rounded", google: "Unbounded:wght@400;500;600;700", stack: "system-ui, sans-serif" },
  { id: "JetBrains Mono", cat: "Monospace", note: "Code · crisp", google: "JetBrains+Mono:wght@400;500;600;700", stack: "ui-monospace, monospace" },
  { id: "Roboto Mono", cat: "Monospace", note: "Code · neutral", google: "Roboto+Mono:wght@400;500;600;700", stack: "ui-monospace, monospace" },
  { id: "IBM Plex Mono", cat: "Monospace", note: "Code · humanist", google: "IBM+Plex+Mono:wght@400;500;600", stack: "ui-monospace, monospace" },
  { id: "System UI", cat: "System", note: "Device default sans", family: "system-ui", stack: "-apple-system, 'Segoe UI', Roboto, sans-serif" },
  { id: "Georgia", cat: "System", note: "Classic serif", family: "Georgia", stack: "'Times New Roman', serif" },
];

const ALIAS: Record<string, string> = { "Source Serif Pro": "Source Serif 4" };

const head = () => document.head || document.getElementsByTagName("head")[0];
const loaded: Record<string, boolean> = {};
const cleanSrc = (s: string) => String(s).replace(/["\\]/g, "");

function addGoogleLink(key: string, spec?: string) {
  if (!spec) return;
  const k = "g:" + key;
  if (loaded[k]) return;
  loaded[k] = true;
  const l = document.createElement("link");
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=" + spec + "&display=swap";
  l.setAttribute("data-jrn-font", key);
  head().appendChild(l);
}
function addFaceStyle(key: string, family: string, src: string) {
  const k = "f:" + key;
  if (loaded[k]) return;
  loaded[k] = true;
  const st = document.createElement("style");
  st.setAttribute("data-jrn-face", key);
  st.textContent = '@font-face{font-family:"' + family + '";src:url("' + cleanSrc(src) + '");font-display:swap;}';
  head().appendChild(st);
}
const specFor = (name: string) => String(name).trim().replace(/\s+/g, "+") + ":wght@400;500;600;700";
function catStack(cat?: string) {
  if (cat === "Serif") return "Georgia, 'Times New Roman', serif";
  if (cat === "Monospace") return "ui-monospace, SFMono-Regular, Menlo, monospace";
  return "system-ui, -apple-system, sans-serif";
}
function byId(id?: string): FontEntry | null {
  if (!id) return null;
  const real = ALIAS[id] || id;
  return BUILTIN.find((f) => f.id === real) || null;
}
function ensure(id?: string) {
  const f = byId(id);
  if (!f) return;
  if (f.google) addGoogleLink(f.id, f.google);
}
function stack(id?: string) {
  const f = byId(id);
  if (f) {
    const prim = f.family ? f.family : '"' + f.id + '"';
    return prim + ", " + (f.stack || catStack(f.cat));
  }
  return '"' + id + '", system-ui, sans-serif';
}
function groups() {
  return CAT_ORDER
    .map((cat) => ({ cat, items: BUILTIN.filter((f) => f.cat === cat) }))
    .filter((g) => g.items.length);
}

export const Fonts = {
  BUILTIN, groups, list: () => BUILTIN.slice(), byId,
  has: (id: string) => !!byId(id),
  ensure, stack, catStack,
};
